import { config } from '@/config';
import MixpanelClient from '@/integrations/Mixpanel/MixpanelClient';
import MixpanelEvent from '@/integrations/Mixpanel/MixpanelEvent';
import MixpanelNoopClient from '@/integrations/Mixpanel/MixpanelNoopClient';
import type { MixpanelClientType } from '@/integrations/Mixpanel/types';
import * as Sentry from '@sentry/vue';

/**
 * All interactions with mixpanel must happen through this class
 */
export default class Mixpanel {
  private static _instance?: Mixpanel;
  private clientMap: Map<boolean, MixpanelClientType>;

  static get instance(): Mixpanel {
    if (!this._instance) {
      this._instance = new Mixpanel();
    }
    return this._instance;
  }

  private constructor() {
    this.clientMap = new Map([
      [true, new MixpanelClient()],
      [false, new MixpanelNoopClient()],
    ]);
  }

  get client() {
    return this.clientMap.get(config.mixpanelEnabled) as MixpanelClientType;
  }

  identify(accountId?: string) {
    try {
      return this.client.identify(accountId);
    } catch (error) {
      Sentry.captureException(error, { extra: { accountId } });
    }
  }

  init() {
    try {
      return this.client.init();
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  reset() {
    return this.client.reset();
  }

  track(event: MixpanelEvent) {
    try {
      return this.client.track(event);
    } catch (error) {
      Sentry.captureException(error, { data: event });
    }
  }

  trackEmailEntered() {
    const event = MixpanelEvent.createInteractionEvent('EmailEntered', 'input_value_updated');
    Mixpanel.instance.track(event);
  }

  trackAreaCodeChanged() {
    const event = MixpanelEvent.createInteractionEvent('AreaCodeChanged', 'option_selected');
    Mixpanel.instance.track(event);
  }

  trackPhoneNumberEntered() {
    const event = MixpanelEvent.createInteractionEvent('PhoneNumberEntered', 'input_value_updated');
    Mixpanel.instance.track(event);
  }

  trackConsentGiven() {
    const event = MixpanelEvent.createInteractionEvent('ConsentGiven', 'button_clicked');
    Mixpanel.instance.track(event);
  }

  trackCountrySelection(value: string) {
    const event = MixpanelEvent.createInteractionEvent('CountrySelected', 'option_selected', value);
    Mixpanel.instance.track(event);
  }

  trackSpendingSpecified(value: string) {
    const event = MixpanelEvent.createInteractionEvent('SpendSpecified', 'option_selected', value);
    Mixpanel.instance.track(event);
  }

  trackAddressValidationSuccess() {
    const event = MixpanelEvent.createInteractionEvent(
      'AddressValidationSucceeded',
      'input_value_updated'
    );
    this.track(event);
  }

  trackAddressValidationFail() {
    const event = MixpanelEvent.createInteractionEvent(
      'AddressValidationFailed',
      'input_value_updated'
    );
    this.track(event);
  }

  trackVerifiableAddressEntered() {
    const event = MixpanelEvent.createInteractionEvent(
      'VerifiableAddressEntered',
      'input_value_updated'
    );
    this.track(event);
  }

  trackUnverifiableAddressEntered() {
    const event = MixpanelEvent.createInteractionEvent(
      'UnverifiableAddressEntered',
      'input_value_updated'
    );
    this.track(event);
  }

  trackManualAddressEntered() {
    const event = MixpanelEvent.createInteractionEvent(
      'ManualAddressEntered',
      'input_value_updated'
    );
    this.track(event);
  }

  trackManualAddressSubmitted() {
    const event = MixpanelEvent.createInteractionEvent('ManualAddressSubmitted', 'button_clicked');
    this.track(event);
  }

  trackManualAddressFormOpened() {
    const event = MixpanelEvent.createInteractionEvent('ManualAddressFormOpened');
    this.track(event);
  }

  trackIdvStepCompleted(
    step:
      | 'DocumentUploadStarted'
      | 'DocumentFrontSideUploaded'
      | 'DocumentBackSideUploaded'
      | 'BiometricsStarted'
      | 'BiometricsCompleted'
  ) {
    const event = MixpanelEvent.createIdvStepsCompletedEvent(step);
    this.track(event);
  }
}
